import type { ICreateOfferScheduleCmd } from "@/data/commands/catalog/CreateOfferScheduleCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { PlanningDao } from "@/data/dao/service/PlanningDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import { OfferScheduleType } from "@/data/model/Enums";
import type { IOffer } from "@/data/model/Offer";
import type { IOfferSchedule } from "@/data/model/OfferSchedule";
import type { IOrderApplication } from "@/data/model/OrderApplication";
import type { IRoomWithService } from "@/data/model/Room";
import { Misc } from "@/data/util/Misc";



import { translate } from "@/shared/localization/language";



import { i18n } from "@/portal/Trans";
import { OfferSchedulesEditor } from "@/portal/elements/OfferSchedulesEditor";
import { DatePicker } from "@/portal/formControls/DatePicker";
import { Select } from "@/portal/formControls/Select";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";


export class OfferScheduleForm extends BaseEntityForm<IOfferSchedule, ICreateOfferScheduleCmd> {
    private typeSelect: Select<OfferScheduleType>;
    private offerInput: Select<IOffer>;
    private roomInput: Select<IRoomWithService>;
    private orderUseCasesInput: Select<IOrderApplication>;
    private fromInput: DatePicker;
    private untilInput: DatePicker;
    private shiftEditor: OfferSchedulesEditor;

    private $internalNoteField: JQuery;

    constructor(element: JQuery) {
        super(element, Factory.getInstance<CatalogDao>(CatalogDao).offerScheduleDataSource);

        const typeSelect = this.$form.find('.form-control[name="scheduletype"]');
        Misc.eachEnum(OfferScheduleType, (i, e) => {
            typeSelect.append('<option value="' + i + '">' + i18n("enums.OfferScheduleType." + e) + "</option>");
        });

        this.typeSelect = Select.createFromSelect(typeSelect);
        this.typeSelect.onChange = () => this.onChangeType();

        this.offerInput = Select.createFromSelect(
            this.$form.find('.form-control[name="offer"]'),
            Factory.getInstance<CatalogDao>(CatalogDao).offerDataSource,
            (c) => c.name,
            false
        );
        this.roomInput = Select.createFromSelect(
            this.$form.find('.form-control[name="rooms"]'),
            Factory.getInstance<RoomDao>(RoomDao),
            (c) => c.name,
            false,
            i18n("offerSchedule.allRooms"),
            undefined,
            true
        );
        this.orderUseCasesInput = Select.createFromSelect(
            this.$form.find('.form-control[name="orderusecases"]'),
            Factory.getInstance<PlanningDao>(PlanningDao).useCaseDataSource,
            (c) => translate(c.name),
            false,
            i18n("general.all"),
            undefined,
            true
        );

        this.fromInput = new DatePicker(this.$form.find('.form-control[name="fromdate"]'), {
            clearBtn: true,
        });
        this.untilInput = new DatePicker(this.$form.find('.form-control[name="untildate"]'), {
            clearBtn: true,
        });

        this.shiftEditor = new OfferSchedulesEditor().replaceWith(this.$form.find(".shifts"));

        //bind range behaviour
        this.fromInput.onChange = (nv) => {
            this.untilInput.setStartDate(nv);
        };

        this.untilInput.onChange = (nv) => {
            this.fromInput.setEndDate(nv);
        };

        this.$internalNoteField = this.$form.find('.form-control[name="internalnote"]');
    }

    private onChangeType() {
        if (parseInt(this.typeSelect.getValue()!) === OfferScheduleType.Closed) {
            this.$form.find(".row.offerrow").hide();
            this.offerInput.setValue(undefined);
        } else {
            if (this.$form.find(".row.offerrow").is(":hidden")) {
                this.offerInput.reset();
            }
            this.$form.find(".row.offerrow").show();
        }
    }

    public setFormData(model?: IOfferSchedule) {
        this.updateName(i18n("general.entities.offerSchedule"));

        this.typeSelect.setValue(model === undefined ? OfferScheduleType.Standard : model.scheduleType);
        this.offerInput.setValue(model === undefined || model.offer === undefined ? undefined : model.offer.id);
        this.roomInput.setValues(model === undefined ? undefined : (model.rooms.map((c) => c.id) as any));
        this.orderUseCasesInput.setValues(
            model === undefined ? undefined : (model.orderUseCases.map((c) => c.id) as any)
        );
        this.fromInput.setValue(model === undefined ? undefined : model.from);
        this.untilInput.setValue(model === undefined ? undefined : model.until);
        this.shiftEditor.setValue(model === undefined ? [] : model.shifts);
        this.$internalNoteField.val(model === undefined || model.internalNote === undefined ? "" : model.internalNote);

        this.onChangeType();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateOfferScheduleCmd {
        const type: OfferScheduleType = parseInt(this.typeSelect.getValue() as any);
        const data: ICreateOfferScheduleCmd = {
            roomIds: this.roomInput.getValues(),
            orderUseCaseIds: this.orderUseCasesInput.getValues(),
            offerId: type === OfferScheduleType.Closed ? undefined : this.offerInput.getValue(),
            scheduleType: type,
            shifts: this.shiftEditor.getValue(),
            from: this.fromInput.getValue(),
            until: this.untilInput.getValue(),
            internalNote: this.$internalNoteField.val().length === 0 ? undefined : this.$internalNoteField.val(),
        };

        let hasError = false;

        if (!this.shiftEditor.validate()) {
            hasError = true;
        }

        if (data.from !== undefined && data.until !== undefined && data.from > data.until) {
            this.untilInput.setError(true);
            hasError = true;
        }

        if (type != OfferScheduleType.Closed && data.offerId == null) {
            hasError = true;
            this.offerInput.setError(true);
        }

        return (hasError ? undefined : data) as any;
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case "offer_schedule_intersection":
                return i18n("offerSchedule.failedPlanningOverlapsOtherPlanning");
        }

        return undefined;
    }
}
